import { Button, Grid, Typography, withStyles, Theme } from '@material-ui/core'
import fastSort from 'fast-sort'
import i18n from 'i18next'
import { path } from 'ramda'
import { FC } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { updateStatusWithForm } from '../../../actions/apartments'
import { openDialog } from '../../../actions/dialogs'
import {
  Navigate,
  RemoveApartment,
  UpdateStatus,
  OpenDialogAction,
  UpdateStatusWithForm,
  UpdateApartmentCode
} from '../../../actions/types'
import { UpdateStatusButtons } from '../../../components/Buttons'
import { Message } from '../../../components/Messages'
import { MuiClasses } from '../../../types'
import { Apartment, ApartmentStatus } from '../../../types/apartments'
import { Application } from '../../../types/applications'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import { getStepPath } from '../../../utils/steps'
import ApplicationsDialog from '../../Applications/ApplicationsList/ApplicationsDialog'
import Dialog from '../../Dialog'
import { UpdateUserId } from './UpdateUserId'

const REACT_APP_INSTABOT_URL = process.env.REACT_APP_INSTABOT_URL

interface DispatchProps {
  openDialogAction: OpenDialogAction
  updateStatusWithFormAction: UpdateStatusWithForm
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  openDialogAction: (id: string) => dispatch(openDialog(id)),
  updateStatusWithFormAction: bindSubmitActionToPromise(
    dispatch,
    updateStatusWithForm
  )
})

interface ApartmentsTableRowCollapseProps {
  classes: MuiClasses
  apartment: Apartment
  t: i18n.TFunction
  removeApartment: RemoveApartment
  navigate: Navigate
  apartmentApplications: Application[]
  updateStatus: UpdateStatus
  updateApartmentCode: UpdateApartmentCode
}

type ComponentProps = ApartmentsTableRowCollapseProps & DispatchProps
const styles = ({ breakpoints, spacing }: Theme) => ({
  primaryBtn: {
    backgroundColor: '#4f9b44',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  }
})
const ApartmentsTableRowCollapse: FC<ComponentProps> = ({
  classes,
  t,
  apartment: {
    userId,
    apartmentId,
    status,
    information,
    metadata,
    code,
    applicationIds
  },
  apartmentApplications,
  removeApartment,
  navigate,
  updateStatus,
  updateApartmentCode,
  updateStatusWithFormAction
}) => {
  const selectedApplication =
    applicationIds && applicationIds?.contractWantedId && apartmentApplications
      ? apartmentApplications.find(
          (application) =>
            application.applicationId === applicationIds.contractWantedId
        )
      : undefined
  const selectedContractWantedName = selectedApplication?.data?.name
    ? selectedApplication?.data?.name
    : undefined
  fastSort(apartmentApplications).by({
    desc: (application) => application.createdAt
  })
  return (
    <Grid container spacing={3}>
      <Grid container item xs={12}>
        <Button
          size='small'
          variant='contained'
          className={classes.primaryBtn}
          color='primary'
          onClick={() =>
            navigate(getStepPath({ userId, apartmentId, metadata }))
          }
        >
          {t('adminApartments.actions.editApartment')}
        </Button>
        <Button
          size='small'
          variant='contained'
          className={classes.primaryBtn}
          color='primary'
          onClick={() =>
            navigate(`/users/${userId}/apartments/${apartmentId}/overview`)
          }
        >
          {t('apartment.list.overview')}
        </Button>
        {status == ApartmentStatus.marketing && (
          <>
            <Button
              size='small'
              variant='contained'
              className={classes.primaryBtn}
              color='primary'
              href={`/new-application/${code}`}
              target='_blank'
            >
              {t('apartments.card.createApplication')}
            </Button>
            <Button
              size='small'
              variant='contained'
              className={classes.primaryBtn}
              color='primary'
              href={`${REACT_APP_INSTABOT_URL}?${code}`}
              target='_blank'
            >
              {t('apartments.card.createApplication')} (Instabot)
            </Button>
            <Button
              size='small'
              variant='contained'
              className={classes.primaryBtn}
              color='primary'
              onClick={() => updateApartmentCode(apartmentId)}
            >
              {t('apartments.card.changeApartmentCode')}
            </Button>
          </>
        )}
        {apartmentApplications && apartmentApplications.length > 0 && (
          <ApplicationsDialog
            apartmentId={apartmentId}
            apartmentUserId={userId}
            applications={apartmentApplications}
            t={t}
            apartmentAddress={path(['address'], information)}
          />
        )}
        {/* <Button
          size='small'
          variant='contained'
          className={classes.primaryBtn}
          color='primary'
          onClick={() => updateApartmentCode(apartmentId)}
        >
          {t('general.chat')}
        </Button> */}
        {(status === ApartmentStatus.confirmed ||
          status === ApartmentStatus.created) &&
          (!apartmentApplications || apartmentApplications?.length === 0) && (
            <Dialog
              dialogId='UpdateApartmentUserId'
              title={t('updateApartmentUserId.dialog.title', {
                address: information?.address || ''
              })}
              buttonLabel={t('updateApartmentUserId.button')}
              Content={<UpdateUserId t={t} />}
            />
          )}
        <Grid item style={{ marginLeft: 'auto' }}>
          <Dialog
            dialogId='ConfirmDeleteDialog'
            title={t('apartments.delete.label')}
            buttonLabel={t('general.delete')}
            color='secondary'
            confirmDialogSubmitAction={() => removeApartment(apartmentId)}
            Content={
              <Message
                message={t('apartments.delete.confirmMessage', {
                  address: path(['address'], information)
                })}
              />
            }
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h5'>
          {t('adminApartments.updateStatusTitle')}
        </Typography>
        <UpdateStatusButtons
          currentStatus={status}
          updateStatus={updateStatus}
          t={t}
          classes={classes}
          updateStatusWithForm={updateStatusWithFormAction}
          selectedContractWantedName={selectedContractWantedName}
          address={path(['address'], information)}
        />
      </Grid>
    </Grid>
  )
}

export default connect(
  null,
  mapDispatchToProps
)(withStyles(styles)(ApartmentsTableRowCollapse))
